<template>
  <div v-title="$route.meta.title">
    <div class="page-heading">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item to="/">首页</el-breadcrumb-item>
        <el-breadcrumb-item>客户管理</el-breadcrumb-item>
        <el-breadcrumb-item>{{ $route.meta.title }}</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <template v-if="showErrorMsg">
      <error-panel v-if="detailErrorMsg" :message="detailErrorMsg" show-back/>
    </template>
    <layout-panel v-else>
      <!-- 顶部项目基本信息 -->
      <div class="project-info-bar">
        <div class="info-item">
          <span class="info-label">项目ID:</span>
          <span class="info-value">{{ projectInfo.project_id }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">项目名称:</span>
          <span class="info-value">{{ projectInfo.project_name }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">项目负责人:</span>
          <span class="info-value">{{ `${projectInfo.create_user_name}/${projectInfo.create_user_id}` }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">立项时间:</span>
          <span class="info-value">{{ projectInfo.create_time }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">项目状态:</span>
          <span class="info-value">{{ projectInfo.project_status_str }}</span>
          <el-tooltip v-if="projectInfo.project_status !== 30"
                      content="项目尚未审核完成，仅可处理 任务上线节点 前的相关进度节点"
                      placement="top">
            <i class="el-icon-question info-tip"></i>
          </el-tooltip>
        </div>
        <div class="info-item" v-if="isProgressHide()">
          <b class="info-label" style="color: red;">当前项目已从列表页隐藏,禁止填写/查看数据</b>
        </div>
      </div>

      <!-- 项目周期节点 -->
      <div class="section-title">项目周期节点</div>
      <!-- 横向步骤条 -->
      <div class="timeline-horizontal-wrapper">
        <div class="timeline-horizontal">
          <div v-for="(node, index) in nodeList"
               :key="index"
               class="timeline-node"
               :class="{ active: node.is_node_finished }">
            <div class="node-icon-wrapper">
              <i :class="node.icon || 'el-icon-s-order'"></i>
            </div>
            <div class="node-label">{{ node.name }}</div>
            <div v-if="node.is_node_optional" class="node-label">(选填)</div>
            <template v-if="canViewNode(node.key)">
              <div class="node-date" v-if="node.tableDateStr">{{ node.tableDateStr }}</div>
              <div class="node-date" v-if="node.tableTimeStr">{{ node.tableTimeStr }}</div>
            </template>
          </div>
        </div>
      </div>

      <!-- 节点卡片列表 -->
      <div class="node-card-list">
        <div v-for="(node, index) in nodeList"
             :key="'card-' + index"
             class="node-card"
             :class="{ done: node.is_node_finished }">
          <div class="card-main">
            <div class="card-title">
              {{ node.name }}
              <span v-if="costConfirmStatus(node)"
                    :style="{color: costConfirmStatus(node).color}">
                （{{ costConfirmStatus(node).text }}）
              </span>
            </div>
            <div class="card-desc">{{ getNodeSubtitle(node) }}</div>
          </div>
          <div class="card-extra">
            <div class="button-group">
              <el-button v-if="showNodeAuditBtn(node)"
                         size="small"
                         type="warning"
                         @click="doAuditNode(node)">确认
              </el-button>
              <el-button v-if="showFillBtn(node)"
                         type="primary"
                         size="small"
                         @click="doFillNode(node)">填写
              </el-button>
              <el-button v-if="showViewBtn(node)"
                         size="small"
                         type="primary"
                         plain
                         @click="doViewNode(node)">查看
              </el-button>
            </div>
            <div v-if="canViewNode(node.key) && node.time_show_in_table"
                 class="card-date">{{ node.time_show_in_table }}
            </div>
          </div>
        </div>
      </div>

      <div>
        <router-link to="/project/progress-table">
          <el-button type="primary">返回列表</el-button>
        </router-link>
      </div>
    </layout-panel>

    <!--项目节点数据填写弹窗-->
    <ProjectNodeDialog :visible.sync="projectNodeDialogVisible"
                       @refreshDetail="fetchDetail(id)"
                       :projectId="id"
                       :fullNode.sync="currentProjectNode"
                       :customerId="projectInfo.customer_id"/>
    <!--项目节点数据查看弹窗-->
    <ProjectNodeView :visible.sync="projectNodeViewVisible"
                     :projectId="id"
                     :fullNode="currentProjectNode"
                     @refreshDetail="fetchDetail(id)"/>
    <!--项目节点数据查看弹窗-->
    <ProjectNodeAudit :visible.sync="projectNodeAuditVisible"
                      :projectId="id"
                      :fullNode="currentProjectNode"
                      @refreshDetail="fetchDetail(id)"/>
  </div>
</template>

<script>
import projectMixins from '@views/project/project.mixins'
import ProjectNodeDialog from '@views/project/pages/ProjectNodeDialog.vue'
import ProjectNodeView from '@views/project/pages/ProjectNodeView.vue'
import ProjectNodeAudit from '@views/project/pages/ProjectNodeAudit.vue'
import ErrorPanel from '@components/ErrorPanel.vue'
import LayoutPanel from '@core/components/LayoutPanel.vue'

export default {
  components: {
    LayoutPanel,
    ErrorPanel,
    ProjectNodeDialog,
    ProjectNodeView,
    ProjectNodeAudit,
  },
  mixins: [projectMixins],
  data () {
    return {
      showErrorMsg: false,
      detailErrorMsg: '',
      id: this.$route.params.id || '0',
      projectInfo: {
        project_id: '',
        project_name: '',
        create_user_name: '',
        create_time: '',
      },
      nodeList: [],
      nodeMap: {},
      currentProjectNode: {},
      projectNodeDialogVisible: false,
      projectNodeViewVisible: false,
      projectNodeAuditVisible: false,
    }
  },
  computed: {
    ...Vuex.mapState({
      loginUser: state => state.login.user,
    }),
  },
  created () {
    if (this.id) {
      this.fetchDetail(this.id)
    }
  },
  methods: {
    ...Vuex.mapActions([]),
    getNodeSubtitle (node) {
      const status = this.costConfirmStatus(node)
      if (!status) return node.subtitle
      const subtitleMap = {
        wait_audit: '需领导确认后方可继续后续节点',
        audit_pass: '项目成本已确认通过',
        audit_reject: '项目成本已被驳回',
      }
      return subtitleMap[status.value] || node.subtitle
    },
    costConfirmStatus (node) {
      if (node.key !== 'project_cost_confirm') return null
      const status = _.get(node, 'project_node.node_result.cost_audit_status', '')
      const statusMap = {
        wait_audit: {value: 'wait_audit', text: '待确认', color: '#e6a23c'},
        audit_pass: {value: 'audit_pass', text: '确认通过', color: '#67c23a'},
        audit_reject: {value: 'audit_reject', text: '确认驳回', color: '#f56c6c'},
        no_audit: {value: 'no_audit', text: '无需确认', color: '#67c23a'},
      }
      if (!status) {
        return {value: '', text: '待填写', color: '#909399'}
      }
      return statusMap[status] || {value: status, text: status, color: '#909399'}
    },
    async fetchDetail (id) {
      const {data} = await request.common('getProjectProgressDetailApi', {id})

      if (!filter.hasCustomer(data.customer_id)) {
        this.detailErrorMsg = `您没有客户(ID:${data.customer_id})的资源权限,无法查看项目进度`
        this.showErrorMsg = true
        return
      }

      this.nodeList = this.handleProjectProgressSteps(data.progress_config || [])
      this.nodeList.forEach((node) => {
        this.nodeMap[node.key] = node
      })
      delete data.progress_config
      this.projectInfo = data
    },
    showFillBtn (node) {
      // 节点不可主动编辑，返回false
      if (!node.editable) {
        return false
      }
      // 当前节点已完成，但是不支持多次填写，返回false
      if (!!node.is_node_finished && !node.allow_multi_fill) {
        return false
      }

      // 项目尚未审核通过, 禁止填写 上线节点 及以后的所有节点
      if (this.projectInfo.project_status !== 30) {
        const allowFillNode = ['upload_customer_list', 'project_cost_confirm', 'kickoff_meeting', 'upload_cleaned_files']
        if (allowFillNode.includes(node.key) === false) {
          return false
        }
      }
      // 成本确认节点未完成时,后面的节点（除了清洗返回）不可进行填写,它前面的节点不限制
      const costNode = this.nodeMap['project_cost_confirm']
      if (costNode && !costNode.is_node_finished && node.key !== 'project_cost_confirm') {
        if (['upload_customer_list', 'upload_cleaned_files'].includes(node.key) === false) {
          return false
        }
      }

      switch (node.key) {
        case 'upload_cleaned_files':
          // 查找客户列表节点,若节点不存在或节点未完成,此节点不允许填写
          if (!this.nodeMap['upload_customer_list'] || !this.nodeMap['upload_customer_list'].is_node_finished) {
            return false
          }
          return _.get(this.nodeMap['upload_customer_list'], 'project_node.node_result.not_clear_files', null) === false
        case 'bo_header_score':
          // 评分节点未完成
          if (!node.is_node_finished) {
            // 上一个节点也未完成，返回false
            if (!node.is_prev_node_finished) {
              return false
            }
            // 允许BO审批人/超管填写BO评分
            return this.loginUser.id + '' === this.projectInfo.bu_approver + '' || this.loginUser.usertype + '' === '1'
          }
          break
        case 'first_audit_score':
          // 指定账号 Ken Zhang/753
          return ['753', 753].includes(this.loginUser.id)
        case 'second_audit_score':
          // 指定账号 Irene Huang/65
          return ['65', 65].includes(this.loginUser.id)
        case 'project_cost_confirm':
          return [0, 3].includes(_.get(node, 'project_node.finished_status', 0))
      }
      // 数据清洗专员,只能处理 清洗返回节点
      if (!this.canDataCleanserOpNode(node.key)) {
        return false
      }
      return true
    },
    async doFillNode (node) {
      if (this.isProgressHide()) {
        this.$message.error('当前进度数据已被隐藏显示,禁止填写')
        return
      }
      switch (node.key) {
        case 'bo_header_score':
          if (this.loginUser.id + '' !== this.projectInfo.bu_approver + '') {
            await this.$confirm(`您并非当前项目指定的BO审核人(${this.projectInfo.bu_approver_name}/${this.projectInfo.bu_approver}),是否要代替BO Header填写此项目的BO评分?`, '提示')
          }
      }
      this.currentProjectNode = node
      if (node.key === 'upload_customer_list') {
        this.currentProjectNode.cleanedFilesNode = this.nodeMap['upload_cleaned_files']
      }
      this.projectNodeDialogVisible = true
    },
    showViewBtn (node) {
      if (!node.nodeViewComponent) {
        return false
      }
      // 数据清洗专员,只能查看 客户清单和清洗返回
      if (!this.canViewNode(node.key)) {
        return false
      }
      switch (node.key) {
        case 'upload_cleaned_files':
          return _.get(node, 'project_node.node_result.cleaned_files', []).length > 0
        case 'project_cost_confirm':
          return _.get(node, 'project_node.finished_status', 0) > 0
      }

      if (!node.is_node_finished) {
        return false
      }
      return true
    },
    showNodeAuditBtn (node) {
      if (!!node.auditable === false) return false
      switch (node.key) {
        case 'project_cost_confirm':
          return filter.hasPermission(14003) && [2].includes(_.get(node, 'project_node.finished_status', 0))
      }
      return false
    },
    canDataCleanserOpNode (nodeKey) {
      if (this.isDataCleanserAccount()) {
        return ['upload_cleaned_files'].includes(nodeKey)
      }
      return true
    },
    canViewNode (nodeKey) {
      if (this.isDataCleanserAccount()) {
        return ['upload_customer_list', 'upload_cleaned_files'].includes(nodeKey)
      }
      return true
    },
    doViewNode (node) {
      if (this.isProgressHide()) {
        this.$message.error('当前进度数据已被隐藏显示,禁止查看')
        return
      }
      this.currentProjectNode = node
      this.projectNodeViewVisible = true
    },
    doAuditNode (node) {
      if (this.isProgressHide()) {
        this.$message.error('当前进度数据已被隐藏显示,禁止确认')
        return
      }
      this.currentProjectNode = node
      this.projectNodeAuditVisible = true
    },
    isProgressHide () {
      return [3, 4].includes(this.projectInfo.show_in_table)
    },
  },
}
</script>

<style lang="scss">
// 全局CSS
</style>

<style lang="scss"
       scoped>
.project-info-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 24px 48px;
  padding: 16px 0 24px;
  border-bottom: 1px solid #ebeef5;
  margin-bottom: 24px;

  .info-item {
    display: flex;
    align-items: center;
    font-size: 14px;

    .info-label {
      color: #606266;
      margin-right: 8px;
    }

    .info-value {
      color: #303133;
      font-weight: 500;
    }

    .info-tip {
      color: #e6a23c;
      margin-left: 6px;
      cursor: pointer;
      font-size: 16px;
    }
  }
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 20px;
}

.timeline-horizontal-wrapper {
  overflow-x: auto;
  margin-bottom: 32px;

  .timeline-horizontal {
    display: flex;
    align-items: flex-start;
    min-width: max-content;
    padding: 8px 4px;

    .timeline-node {
      display: flex;
      flex-direction: column;
      align-items: center;
      position: relative;
      flex: 1;
      min-width: 80px;

      &:not(:last-child)::after {
        content: '';
        position: absolute;
        top: 16px;
        left: 50%;
        width: 100%;
        height: 2px;
        background-color: #dcdfe6;
        z-index: 0;
      }

      &.active:not(:last-child)::after,
      &.current:not(:last-child)::after {
        background-color: #409eff;
      }

      .node-icon-wrapper {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background-color: #f5f7fa;
        border: 2px solid #dcdfe6;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        color: #909399;
        z-index: 1;
        margin-bottom: 8px;
      }

      &.active .node-icon-wrapper {
        background-color: #ecf5ff;
        border-color: #409eff;
        color: #409eff;
      }

      &.current .node-icon-wrapper {
        background-color: #409eff;
        border-color: #409eff;
        color: #fff;
      }

      .node-label {
        font-size: 12px;
        color: #606266;
        text-align: center;
        line-height: 1.4;
      }

      .node-date {
        font-size: 12px;
        color: #67c23a;
        margin-top: 4px;
      }
    }
  }
}

.node-card-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;

  .node-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    background-color: #fff;
    transition: box-shadow 0.2s;

    &:hover {
      box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
    }

    &.done {
      border-left: 4px solid #409eff;
    }

    &.current {
      border-left: 4px solid #67c23a;
    }

    .card-main {
      .card-title {
        font-size: 14px;
        font-weight: 600;
        color: #303133;
        margin-bottom: 6px;
      }

      .card-desc {
        font-size: 12px;
        color: #909399;
      }
    }

    .card-extra {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 8px;
      flex-shrink: 0;

      .button-group {
        display: flex;
        gap: 8px;
      }

      .card-date {
        font-size: 14px;
        color: #67c23a;
        font-weight: 500;
      }
    }
  }
}
</style>

<style lang="scss"
       module>
// 带作用域的CSS
// 绑定class方式 <p :class="$style.red"></p>
// https://vue-loader.vuejs.org/zh-cn/features/css-modules.html
</style>



// WEBPACK FOOTER //
// src/views/project/pages/ProjectProgressDetail.vue